import { CommentModal } from "@/controls/ag-grid/components/CommentModal";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { BucketAutocomplete } from "@/shared/autocompletes/BucketAutocomplete";
import { RecipeAutocomplete } from "@/shared/autocompletes/RecipeAutocomplete";
import { DateRangeFilter } from "@/shared/DateRangeFilter";
import { TextFieldSearchFilter } from "@/shared/TextFieldSearchFilter";
import { CHARGE_NUMBERS } from "@/utils/constants";
import {
  dxtToLocalServerTime,
  ErrorModal,
  getMomentTz,
  HelmetDexteel,
  MesfModal,
  useContextMenuMESF,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
  RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Group, Panel } from "react-resizable-panels";
import { useResizablePanels } from "@/pages/sy/hooks/useResizablePanels";
import { ResizablePanelSeparator } from "@/pages/sy/components/ResizablePanelSeparator";
import { getChargeStatusLabel } from "@/pages/sy/constants/chargeStatus";
import { useBucketsQuery } from "@/pages/sy/hooks/queries/useBucketsQuery";
import { useChargeByIdQuery } from "@/pages/sy/hooks/queries/useChargeByIdQuery";
import { useChargeOptionsFunctions } from "../../hooks/useChargeOptionsFunctions";
import { useTableData } from "../../hooks/useTableData";
import { Charge } from "@/pages/sy/models/Charge";
import { getChargesForExport } from "@/pages/sy/repositories/ChargeRepository";
import { useFilteredChargesQuery } from "@/pages/sy/hooks/queries/useFilteredChargesQuery";

import {
  useDeleteChargeMutation,
  useUpsertChargeMutation,
  useCreateGroupNumberMutation,
} from "@/pages/sy/hooks/queries/useChargeMutations";
import { getErrorMessage } from "@/shared/hooks/getErrorMessage";
import {
  DEFAULT_FORM_VALUES,
  validateBucket,
  validateScheduledHeat,
  type BucketSelection,
  type ChargeSelection,
  type FormValues,
} from "../../utils/validations";
import { ChargeDetails } from "./ChargeDetails";

const moment = getMomentTz();

type Props = {
  chargeId: string | undefined;
};

export const TableCharge = ({ chargeId }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: buckets = [] } = useBucketsQuery();

  // === TanStack Query Mutations ===
  const deleteChargeMutation = useDeleteChargeMutation();
  const upsertChargeMutation = useUpsertChargeMutation();
  const createGroupNumberMutation = useCreateGroupNumberMutation();

  const onMutationError = (err: unknown) => setError(getErrorMessage(err));

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [charge, setCharge] = useState<Charge | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [error, setError] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");

  // Combined loading state for create operations
  const isCreating =
    upsertChargeMutation.isPending || createGroupNumberMutation.isPending;
  const [calculatedChargeNumber, setCalculatedChargeNumber] = useState<
    number | null
  >(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedChargeForComment, setSelectedChargeForComment] =
    useState<Charge | null>(null);
  const [positionToOpenModal, setPositionToOpenModal] = useState<{
    top: number;
    left: number;
  }>();
  const isGridReady = useRef(false);
  const [isGridDataReady, setIsGridDataReady] = useState(false);
  const [pendingSelectChargeId, setPendingSelectChargeId] = useState<
    number | null
  >(null);

  // Date filter states
  const [selectedDate, setSelectedDate] = useState(() => moment());
  const [selectedDays, setSelectedDays] = useState(1);

  // Adapt the date filter to the charge from the URL, once, on first load
  const initialChargeIdRef = useRef(
    chargeId && chargeId !== "new" ? parseInt(chargeId, 10) : null,
  );
  const [urlChargeHandled, setUrlChargeHandled] = useState(
    initialChargeIdRef.current === null,
  );
  const { data: urlCharge } = useChargeByIdQuery(
    urlChargeHandled ? null : initialChargeIdRef.current,
  );

  useEffect(() => {
    if (urlChargeHandled || urlCharge === undefined) return;

    if (urlCharge) {
      const creationDay = dxtToLocalServerTime(
        urlCharge.CreationTimestamp,
        "yyyy-MM-dd",
      );
      if (creationDay) {
        const day = moment(creationDay, "YYYY-MM-DD");
        if (!selectedDate.isSame(day, "day")) {
          setSelectedDate(day);
        }
      }
    }
    setUrlChargeHandled(true);
  }, [urlCharge, urlChargeHandled]);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  // Resizable panels
  const { groupRef, defaultLayout, onLayoutChanged, resetLayout } =
    useResizablePanels({
      groupId: "charge-management-panels",
      defaultLayout: { table: 40, details: 60 },
    });

  const { state } = useUTLSettingsContext();
  const refreshTime = parseInt(
    get(state.settings, "SCRAP_CHARGES_MANAGEMENT_REFRESH_TIME", 30),
  );

  // === TanStack Query: date-filtered charges ===
  const startDate = useMemo(
    () =>
      selectedDate
        .clone()
        .subtract(selectedDays, "days")
        .startOf("day")
        .toDate(),
    [selectedDate, selectedDays],
  );
  const endDate = useMemo(
    () =>
      selectedDate.clone().add(selectedDays, "days").startOf("day").toDate(),
    [selectedDate, selectedDays],
  );

  const { data: charges = [], isLoading } = useFilteredChargesQuery(
    startDate,
    endDate,
    refreshTime,
  );

  // Sync selected charge with latest query data
  const selectedChargeId = charge?.ChargeId;
  useEffect(() => {
    if (!selectedChargeId) return;
    const updated = charges.find((c) => c.ChargeId === selectedChargeId);
    if (updated) setCharge(updated);
  }, [charges, selectedChargeId]);

  const invalidateCharges = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["charges"] });
  }, [queryClient]);

  // Handle pending charge selection when data arrives
  useEffect(() => {
    if (pendingSelectChargeId && gridApi) {
      const rowNode = gridApi.getRowNode(String(pendingSelectChargeId));
      if (rowNode) {
        gridApi.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
        rowNode.setSelected(true);
        setCharge(rowNode.data);
        setPendingSelectChargeId(null);
      }
    }
  }, [charges, pendingSelectChargeId, gridApi]);

  const handleDateChange = (newValue: moment.Moment | null) => {
    const dateToSet = newValue || moment();
    if (!selectedDate.isSame(dateToSet, "day")) {
      setSelectedDate(dateToSet);
    }
  };

  const handleDaysChange = (newDays: number) => {
    if (newDays !== selectedDays) {
      setSelectedDays(newDays);
    }
  };

  const handleNavigate = (direction: "prev" | "next") => {
    const daysToMove = direction === "next" ? selectedDays : -selectedDays;
    const newDate = selectedDate.clone().add(daysToMove, "days");
    setSelectedDate(newDate);
  };

  const { control, handleSubmit, watch, reset, trigger } = useForm<FormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onChange",
  });

  const handleNewCharge = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setCharge(null);
    navigate(`/scrap-charges/new`);

    // Set default bucket values: 1=B, 2=C, 3=A
    const bucketB = buckets.find(
      (b) => b.BucketName?.toUpperCase().trim() === "B",
    );
    const bucketC = buckets.find(
      (b) => b.BucketName?.toUpperCase().trim() === "C",
    );
    const bucketA = buckets.find(
      (b) => b.BucketName?.toUpperCase().trim() === "A",
    );

    reset({
      ...DEFAULT_FORM_VALUES,
      selectedBuckets: {
        bucket1: bucketB?.BucketId ?? null,
        bucket2: bucketC?.BucketId ?? null,
        bucket3: bucketA?.BucketId ?? null,
      },
    });

    // Reset calculated charge number
    setCalculatedChargeNumber(null);
  };

  const handleHeatCheck = (heatName: string) => {
    if (!heatName.trim()) {
      setCalculatedChargeNumber(null);
      return;
    }

    const heatCharges = charges.filter(
      (c: Charge) =>
        c.ScheduledHeatName?.toUpperCase() === heatName.toUpperCase(),
    );

    if (heatCharges.length > 0) {
      const maxChargeNumber = Math.max(
        ...heatCharges.map((c: Charge) => c.ChargeNumber || 0),
      );
      setCalculatedChargeNumber(maxChargeNumber + 1);
    } else {
      // New heat, start with charge #1
      setCalculatedChargeNumber(1);
    }
  };

  const handleDeleteCharge = () => {
    if (!charge) return;

    deleteChargeMutation.mutate(charge.ChargeId as number, {
      onSuccess: () => {
        onModalHide({ shouldUpdate: true, close: true });
      },
      onError: onMutationError,
    });
  };

  const handleSearchChange = (val: string) => {
    setFilterValue(val);
    gridApi?.setGridOption("quickFilterText", val);
  };

  const handleExportToExcel = async () => {
    if (!gridApi) {
      setNotificationMessage("Please wait until the table is loaded");
      return;
    }

    setIsExporting(true);

    try {
      const resp = await getChargesForExport(startDate, endDate);
      if (!resp.ok) {
        setError(resp.message || "Failed to fetch charges for export");
        return;
      }

      // Collect all unique commodity names across all rows
      const allCommodityNames = new Set<string>();

      // Parse JSON and collect all commodity names
      const rawRows = (resp.data?.tables?.[0]?.rows || []).map(
        (row: Record<string, unknown>) => {
          const { CommodityWeights, ...baseRow } = row;
          const flattenedRow: Record<string, unknown> = {
            ...baseRow,
            ChargeStatusCode:
              typeof row.ChargeStatusCode === "string"
                ? getChargeStatusLabel(row.ChargeStatusCode)
                : row.ChargeStatusCode,
          };

          // Parse CommodityWeights JSON and add as individual columns
          if (CommodityWeights && typeof CommodityWeights === "string") {
            try {
              const commodities = JSON.parse(CommodityWeights) as Array<{
                CommodityName: string;
                Weight: number;
              }>;
              commodities.forEach(({ CommodityName, Weight }) => {
                flattenedRow[CommodityName] = Weight;
                allCommodityNames.add(CommodityName);
              });
            } catch (e) {
              setError(`Failed to parse commodity data: ${e}`);
              return flattenedRow;
            }
          }
          return flattenedRow;
        },
      );

      // All commodity columns sorted alphabetically
      const allCommodities = Array.from(allCommodityNames).sort();

      // Fill missing commodities with 0 for rows that don't have them
      const exportCharges = rawRows.map((row: Record<string, unknown>) => {
        const filledRow = { ...row };
        allCommodities.forEach((commodity) => {
          if (!(commodity in filledRow)) {
            filledRow[commodity] = 0;
          }
        });
        return filledRow;
      });

      // Get base column info from the grid (field + header name)
      const originalColumnDefs = gridApi.getColumnDefs() || [];
      const baseColumns = originalColumnDefs
        .filter(
          (col: ColDef) =>
            col.headerName &&
            col.headerName.trim() !== "" &&
            col.headerName !== "Status Text",
        )
        .map((col: ColDef) => ({
          field: col.field || "",
          headerName: col.headerName || "",
        }));

      // Full column list: base columns + commodity columns
      const allColumns = [
        ...baseColumns,
        ...allCommodities.map((name) => ({
          field: name,
          headerName: name,
        })),
      ];

      // Build Excel content entirely from data (bypasses ag-Grid column model
      // which doesn't reliably register dynamically-added columns for export)
      const startDateStr = moment(startDate).format("YYYY-MM-DD");
      const endDateStr = moment(endDate)
        .subtract(1, "day")
        .format("YYYY-MM-DD");

      const prependContent: {
        cells: Array<{
          data: { type: "String"; value: string };
          mergeAcross?: number;
          styleId?: string;
        }>;
      }[] = [
        {
          cells: [
            {
              styleId: "header",
              data: { type: "String" as const, value: "Charges" },
              mergeAcross: 4,
            },
          ],
        },
        {
          cells: [
            {
              data: {
                type: "String" as const,
                value: `Date: ${startDateStr} to ${endDateStr}`,
              },
              mergeAcross: 3,
            },
          ],
        },
        { cells: [] },
        // Column header row
        {
          cells: allColumns.map((col) => ({
            styleId: "header",
            data: { type: "String" as const, value: col.headerName },
          })),
        },
        // Data rows
        ...exportCharges.map((row: Record<string, unknown>) => ({
          cells: allColumns.map((col) => {
            const value = row[col.field];
            return {
              data: {
                type: "String" as const,
                value: value != null ? String(value) : "",
              },
            };
          }),
        })),
      ];

      const appendContent = [
        { cells: [] },
        {
          cells: [
            {
              data: {
                type: "String" as const,
                value: `Generated on: ${new Date().toLocaleString()}`,
              },
              mergeAcross: 3,
            },
          ],
        },
        {
          cells: [
            {
              data: {
                type: "String" as const,
                value: `Total Records: ${exportCharges?.length || 0}`,
              },
              mergeAcross: 3,
            },
          ],
        },
      ];

      // Export with all content as custom rows, skip grid's own columns/data
      gridApi.exportDataAsExcel({
        fileName: `charges_management_${startDateStr}_to_${endDateStr}.xlsx`,
        sheetName: "Charges",
        skipColumnHeaders: true,
        shouldRowBeSkipped: () => true,
        columnWidth: 120,
        prependContent,
        appendContent,
      });

      setNotificationMessage("Export to Excel completed successfully");
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setIsExporting(false);
    }
  };

  const handleShowCommentModal = (
    show: boolean,
    chargeData?: Charge,
    position?: { top: number; left: number },
  ) => {
    setPositionToOpenModal(position);
    setShowCommentModal(show);
    if (chargeData) {
      setSelectedChargeForComment(chargeData);
    }
  };

  const { getMenuOptions } = useChargeOptionsFunctions({
    setCharge,
    setShowModal,
    handleNewCharge,
  });

  const { columnDefs } = useTableData({
    setCharge,
    setShowModal,
    charges,
    setShowCommentModal: handleShowCommentModal,
  });

  const chargeTypeWatch = watch("chargeType");

  const onRowSelected = (event: RowSelectedEvent) => {
    if (event.node.isSelected()) {
      setCharge(event.data);
      setShowModal("edit");
      navigate(`/scrap-charges/${(event.data as Charge).ChargeId}`);
    }
  };

  const refreshDataIfNeeded = useCallback(
    (_force = false, _showLoader = false) => {
      invalidateCharges();
      return true;
    },
    [invalidateCharges],
  );

  const onModalHide = ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => {
    if (close) {
      setShowModal("");
      setCharge(null);
      gridApi?.deselectAll();
      navigate(`/scrap-charges`);
      return;
    }

    if (!entityId || !shouldUpdate) return;

    setShowModal("edit");
    navigate(`/scrap-charges/${entityId}`);

    findAndSelectChargeById(entityId);
  };

  const findAndSelectChargeById = (chargeId: number) => {
    const rowNode = gridApi?.getRowNode(String(chargeId));

    if (rowNode) {
      selectChargeNode(rowNode);
      setPendingSelectChargeId(null);
      return;
    }

    // Node not found - store for pending selection and invalidate to refresh
    setPendingSelectChargeId(chargeId);
    invalidateCharges();
  };

  const selectChargeNode = (node: IRowNode) => {
    gridApi?.ensureIndexVisible(node.rowIndex ?? 0, "middle");
    node.setSelected(true);
    setCharge(node.data);
  };

  const getContextMenuItems = (
    params: GetContextMenuItemsParams,
  ): (DefaultMenuItem | MenuItemDef)[] => {
    const data = params.node?.data;

    if (data) {
      params.api.deselectAll();
      params.node?.setSelected(true);
      setCharge(data);
    }

    showContextMenu(
      params.event as unknown as React.MouseEvent,
      data,
      "chargeContext",
    );

    return [];
  };

  const onSubmit = (data: FormValues) => {
    if (data.chargeType === "unlinked") {
      if (!data.selectedBucketId || !calculatedChargeNumber) {
        setError("Please enter a heat name and select a bucket");
        return;
      }

      const newCharge: Partial<Charge> = {
        ChargeStatusCode: "S",
        BucketId: data.selectedBucketId,
        ChargeNumber: calculatedChargeNumber,
        ScheduledHeatName: data.scheduledHeat ? data.scheduledHeat : null,
      };

      upsertChargeMutation.mutate(newCharge as Charge, {
        onSuccess: () => {
          setShowModal("");
        },
        onError: onMutationError,
      });
      return;
    }

    const hasSelectedCharges = CHARGE_NUMBERS.some(
      (idx) => data.selectedCharges[`charge${idx}` as keyof ChargeSelection],
    );

    if (!hasSelectedCharges) {
      setError("At least one charge must be selected");
      return;
    }

    for (const idx of CHARGE_NUMBERS) {
      const chargeKey = `charge${idx}` as const;
      const bucketKey = `bucket${idx}` as const;

      if (data.selectedCharges[chargeKey] && !data.selectedBuckets[bucketKey]) {
        setError(`Bucket must be selected for charge ${idx}`);
        return;
      }
    }

    createGroupNumberMutation.mutate(
      {
        recipeId: data.selectedRecipeId!,
        charge1: data.selectedCharges.charge1,
        charge2: data.selectedCharges.charge2,
        charge3: data.selectedCharges.charge3,
        bucketId1: data.selectedBuckets.bucket1 || 0,
        bucketId2: data.selectedBuckets.bucket2 || 0,
        bucketId3: data.selectedBuckets.bucket3 || 0,
        scheduledHeat: data.scheduledHeat || null,
      },
      {
        onSuccess: () => {
          setShowModal("");
          setNotificationMessage(`Successfully created charges`);
        },
        onError: onMutationError,
      },
    );
  };

  useEffect(() => {
    if (chargeId) {
      if (chargeId === "new") {
        handleNewCharge();
      } else {
        const chargeIdNum = Number(chargeId);
        let chargeNode: IRowNode<Charge> | undefined;

        gridApi?.forEachNode((n) => {
          if ((n.data as Charge).ChargeId === chargeIdNum) {
            chargeNode = n;
            return true;
          }
        });

        if (chargeNode) {
          chargeNode.setSelected(true);
          setCharge(chargeNode.data ?? null);
          setShowModal("edit");
        } else {
          refreshDataIfNeeded(true);
        }
      }
    }
  }, [isLoading, gridApi, chargeId]);

  useEffect(() => {
    if (gridApi && charges.length) {
      gridApi.setGridOption("rowData", charges);
    }
  }, [gridApi, charges]);

  const getMenuOptionsRef = useRef(getMenuOptions);
  getMenuOptionsRef.current = getMenuOptions;

  useEffect(() => {
    registerConfig({
      id: "chargeContext",
      getOptions: (...args: [Charge]) => getMenuOptionsRef.current(...args),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // registerConfig is intentionally excluded — it's an unstable reference from
  // ContextMenuMESFProvider (not wrapped in useCallback). Including it causes an
  // infinite loop: registerConfig() → setConfigs() → provider re-renders →
  // new registerConfig ref → effect re-fires. The ref pattern above ensures
  // getMenuOptions always uses the latest closure.

  // No useEffect needed for date filter changes — TanStack Query re-fetches
  // automatically when startDate/endDate change (they're part of the query key).

  // Watch for charge type changes and reset calculated charge number
  useEffect(() => {
    if (chargeTypeWatch !== "unlinked") {
      setCalculatedChargeNumber(null);
    }
  }, [chargeTypeWatch]);

  return (
    <>
      <HelmetDexteel title="Charges Management" />
      <Box sx={{ p: 2 }} onContextMenu={(e) => e.preventDefault()}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Charges Management
          </Typography>
          <ExcelButton
            handleExportToExcel={handleExportToExcel}
            disabled={!gridApi || isLoading || isExporting || !isGridDataReady}
            label={isExporting ? "EXPORTING..." : "EXPORT TO EXCEL"}
          />
        </Box>

        <Group
          id="charge-management-panels"
          orientation="horizontal"
          onLayoutChanged={onLayoutChanged}
          groupRef={groupRef}
          defaultLayout={defaultLayout}
          style={{ height: "calc(100vh - 140px)" }}
        >
          <Panel id="table" minSize={20}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Grid container spacing={1} alignItems="center">
                  <DateRangeFilter
                    selectedDate={selectedDate}
                    selectedDays={selectedDays}
                    onDateChange={handleDateChange}
                    onDaysChange={handleDaysChange}
                    onNavigate={handleNavigate}
                  />
                  <Grid item xs>
                    <TextFieldSearchFilter
                      value={filterValue}
                      onChange={handleSearchChange}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Box
                sx={{
                  flex: 1,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <AgGridReact<Charge>
                  rowData={charges}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  onGridReady={(params) => setGridApi(params.api)}
                  onFirstDataRendered={() => {
                    isGridReady.current = true;
                    setIsGridDataReady(true);
                  }}
                  getRowId={(params) => String(params.data.ChargeId)}
                  onRowSelected={onRowSelected}
                  quickFilterText={filterValue}
                  includeHiddenColumnsInQuickFilter={true}
                  getContextMenuItems={getContextMenuItems}
                  {...defaultGridOptions}
                  suppressContextMenu={false}
                  loading={isLoading}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNewCharge}
                >
                  NEW CHARGE
                </Button>
              </Box>
            </Stack>
          </Panel>

          <ResizablePanelSeparator onDoubleClick={resetLayout} />

          <Panel id="details" minSize={25}>
            <Paper
              elevation={1}
              sx={{
                height: "100%",
                overflow: "hidden",
              }}
            >
              <ChargeDetails
                charge={charge}
                modal={showModal}
                refetchInterval={
                  charge?.ChargeStatusCode === "O" ? refreshTime : 0
                }
                onHide={onModalHide}
              />
            </Paper>
          </Panel>
        </Group>

        <MesfModal
          title="NEW CHARGE"
          open={showModal === "new"}
          handleClose={() => {
            setShowModal("");
          }}
          maxWidth="sm"
        >
          <MesfModal.Content sx={{ p: 3 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <Grid container alignItems="center">
                  <Grid item xs={3}>
                    <Typography variant="subtitle1">Scheduled Heat</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Controller
                      name="scheduledHeat"
                      control={control}
                      rules={{
                        validate: validateScheduledHeat,
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          label="Enter a Heat Name"
                          variant="outlined"
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e.target.value.toUpperCase());
                          }}
                          onBlur={() => {
                            field.onBlur();
                            handleHeatCheck(field.value || "");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleHeatCheck(field.value || "");
                            }
                          }}
                          fullWidth
                          size="small"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          slotProps={{
                            htmlInput: { maxLength: 6 },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container alignItems="center">
                  <Grid item xs={3}>
                    <Typography variant="subtitle1">Charge Type</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Controller
                      name="chargeType"
                      control={control}
                      render={({ field }) => (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight:
                                field.value === "unlinked" ? "bold" : "normal",
                              color:
                                field.value === "unlinked"
                                  ? "primary.main"
                                  : "text.secondary",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={() => field.onChange("unlinked")}
                          >
                            Unlinked
                          </Typography>
                          <Switch
                            checked={field.value === "fromRecipe"}
                            onChange={(event) => {
                              field.onChange(
                                event.target.checked
                                  ? "fromRecipe"
                                  : "unlinked",
                              );
                            }}
                            color="primary"
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight:
                                field.value === "fromRecipe"
                                  ? "bold"
                                  : "normal",
                              color:
                                field.value === "fromRecipe"
                                  ? "primary.main"
                                  : "text.secondary",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={() => field.onChange("fromRecipe")}
                          >
                            From Recipe
                          </Typography>
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>

                {chargeTypeWatch === "fromRecipe" && (
                  <Grid container alignItems="center">
                    <Grid item xs={3}>
                      <Typography variant="subtitle1">Recipe</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Controller
                        name="selectedRecipeId"
                        control={control}
                        rules={{
                          required: "Recipe is required",
                        }}
                        render={({ field, fieldState }) => (
                          <RecipeAutocomplete
                            label="Select a Recipe"
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                              trigger("selectedCharges");
                            }}
                            fieldError={fieldState.error}
                            disabled={isLoading}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                )}

                <Grid container alignItems="center">
                  <Grid item xs={3}>
                    <Typography variant="subtitle1">
                      {chargeTypeWatch === "unlinked"
                        ? "Select Bucket"
                        : "Select Charges"}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    {chargeTypeWatch === "unlinked" ? (
                      <Controller
                        name="selectedBucketId"
                        control={control}
                        rules={{
                          validate: validateBucket,
                        }}
                        render={({ field, fieldState }) => (
                          <>
                            <BucketAutocomplete
                              label="Bucket"
                              value={field.value}
                              onChange={field.onChange}
                              disabled={isLoading}
                              defaultSelection="Unknown"
                            />
                            {fieldState.error && (
                              <Typography color="error" variant="caption">
                                {fieldState.error.message}
                              </Typography>
                            )}
                          </>
                        )}
                      />
                    ) : (
                      <Controller
                        name="selectedCharges"
                        control={control}
                        render={() => (
                          <Stack spacing={1.5}>
                            {CHARGE_NUMBERS.map((idx) => {
                              const chargeKey =
                                `charge${idx}` as keyof ChargeSelection;
                              const bucketKey =
                                `bucket${idx}` as keyof BucketSelection;

                              return (
                                <Grid container alignItems="center" key={idx}>
                                  <Grid
                                    item
                                    xs={4}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Controller
                                      name={`selectedCharges.${chargeKey}`}
                                      control={control}
                                      render={({ field }) => (
                                        <input
                                          type="checkbox"
                                          checked={!!field.value}
                                          onChange={(e) => {
                                            field.onChange(e.target.checked);
                                            trigger(
                                              `selectedBuckets.${bucketKey}`,
                                            );
                                            trigger("selectedCharges");
                                          }}
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            cursor: "pointer",
                                          }}
                                        />
                                      )}
                                    />
                                    <Typography
                                      sx={{ ml: 1.25 }}
                                    >{`Charge ${idx}`}</Typography>
                                  </Grid>
                                  <Grid item xs={8}>
                                    <Controller
                                      name={`selectedBuckets.${bucketKey}`}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <>
                                          <BucketAutocomplete
                                            label="Bucket"
                                            value={field.value}
                                            onChange={field.onChange}
                                            disabled={
                                              !watch(
                                                `selectedCharges.${chargeKey}`,
                                              )
                                            }
                                            defaultSelection="Unknown"
                                          />
                                          {fieldState.error && (
                                            <Typography
                                              color="error"
                                              variant="caption"
                                            >
                                              {fieldState.error.message}
                                            </Typography>
                                          )}
                                        </>
                                      )}
                                    />
                                  </Grid>
                                </Grid>
                              );
                            })}
                          </Stack>
                        )}
                      />
                    )}
                  </Grid>
                </Grid>

                {chargeTypeWatch === "unlinked" && (
                  <Grid container alignItems="center">
                    <Grid item xs={3}>
                      <Typography variant="subtitle1">Charge Number</Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: calculatedChargeNumber
                            ? "primary.main"
                            : "text.disabled",
                        }}
                      >
                        {calculatedChargeNumber || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Stack>
              <MesfModal.Actions sx={{ mt: 3 }}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item md={3} xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="inherit"
                      onClick={() => {
                        setShowModal("");
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <Button
                      fullWidth
                      startIcon={
                        isCreating ? <CircularProgress size="1rem" /> : <></>
                      }
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={
                        isLoading ||
                        isCreating ||
                        (chargeTypeWatch === "unlinked" &&
                          !calculatedChargeNumber)
                      }
                    >
                      CREATE
                    </Button>
                  </Grid>
                </Grid>
              </MesfModal.Actions>
            </form>
          </MesfModal.Content>
        </MesfModal>

        <MesfModal
          title="CONFIRM DELETE"
          open={showModal === "delete"}
          handleClose={() => {
            setShowModal("");
          }}
          maxWidth="sm"
        >
          <MesfModal.Content sx={{ p: 4 }}>
            <Typography variant="body1">
              Are you sure you want to delete charge {charge?.ChargeId}? This
              action cannot be undone.
            </Typography>
          </MesfModal.Content>
          <MesfModal.Actions sx={{ p: "20px 30px" }}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item md={3} xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => {
                    setShowModal("");
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item md={3} xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={handleDeleteCharge}
                  disabled={isLoading}
                >
                  DELETE
                </Button>
              </Grid>
            </Grid>
          </MesfModal.Actions>
        </MesfModal>
        <NotificationSnackBar
          message={notificationMessage}
          onHide={() => setNotificationMessage("")}
        />
        <ErrorModal error={error} onHide={() => setError("")} />
        <CommentModal
          show={showCommentModal}
          readOnly
          onHide={() => {
            setShowCommentModal(false);
          }}
          refreshData={() => {
            refreshDataIfNeeded(true, false);
          }}
          position={positionToOpenModal}
          selectedElement={
            selectedChargeForComment
              ? {
                  ...selectedChargeForComment,
                  ElementId: selectedChargeForComment.ChargeId ?? 0,
                }
              : null
          }
          setSelectedElement={setSelectedChargeForComment}
          upsertFunction={async (chargeId, _field, value) => {
            const chargeData = charges.find((c) => c.ChargeId === chargeId);
            if (!chargeData) return { ok: false, message: "Charge not found" };
            try {
              await upsertChargeMutation.mutateAsync({
                ...chargeData,
                Comments: value as string,
              });
              return { ok: true, message: "" };
            } catch (err) {
              return {
                ok: false,
                message: err instanceof Error ? err.message : String(err),
              };
            }
          }}
        />
      </Box>
    </>
  );
};
